import { useTranslation, Trans } from 'react-i18next';
import React, { useState, useMemo, useCallback } from 'react';
import cx from 'clsx';
import dayjs from 'dayjs';

import {
  IconRcStreamDrawHot,
  IconRcStreamDrawWinner,
  IconRcStreamPrizeCash,
  IconRcStreamPrizeCustom,
} from '@/assets/stream';
import { Button, HelperTooltip, OrgVerifiedLogo, RestrictionTip } from '@/components';
import { formatNetworth, formatNum, isOfficalAddress, login } from '@/utils';
import {
  useSwitch,
  useCurrentAddressInfo,
  useDeviceTypeIsMobile,
  useTheme,
  OfficialAccountVerifyStatus,
} from '@/hooks';
import { FeedArticle } from '@/views/Stream';
import { MobileModalTip } from '@/components/shared/MobileModalTip';

import { JoinDrawModal } from '../JoinDrawModal';
import { DrawJoinersModal } from '../DrawJoinersModal';
import style from './index.module.scss';

export const DrawCard = ({ article, isDetails }: { article: FeedArticle; isDetails?: boolean }) => {
  const { t } = useTranslation();

  const isMobile = useDeviceTypeIsMobile();
  const { isDark } = useTheme();
  const { on: mobileTip, turnOn: showMobileTip, turn: setMobileTip } = useSwitch();

  const { isVerified, address } = useCurrentAddressInfo();
  const { on, turnOn, turnOff } = useSwitch();
  const [isWinner, setIsWinner] = useState(false);
  const { on: isUserList, turnOn: showUserList, turnOff: hideUserList } = useSwitch();
  const draw = useMemo(() => article.draw!, [article.draw]);
  const isOfficialCreator = useMemo(
    () => isOfficalAddress(article.creator_id),
    [article.creator_id],
  );
  const hasPermission = useMemo(
    () => article?.draw?.has_permission,
    [article?.draw?.has_permission],
  );

  const leftTime = useMemo(
    () => article.create_at + article.draw!.duration - Date.now() / 1000,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [article.create_at, article.draw!.duration],
  );

  const endsTime = useMemo(
    () => {
      const time = dayjs.unix(article.create_at + article.draw!.duration);

      if (time.isToday()) {
        return `${t('DrawCard.today')} ${time.format('HH:mm')}`;
      }

      if (time.isTomorrow()) {
        return `${t('DrawCard.tomorrow')} ${time.format('HH:mm')}`;
      }

      return time.format('MM/DD HH:mm');
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [article.create_at, article.draw!.duration],
  );

  const isComplete = useMemo(() => leftTime <= 0, [leftTime]);

  const handleJoinClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();

      if (isMobile) {
        showMobileTip();
        return;
      }

      if (!isVerified) {
        login();
        return;
      }

      turnOn();
    },
    [turnOn, isVerified, isMobile, showMobileTip],
  );

  const handleUserListClick = useCallback(
    (e: React.MouseEvent<HTMLSpanElement>) => {
      e.stopPropagation();

      if (isMobile) {
        showMobileTip();
        return;
      }

      const { type } = e.currentTarget.dataset;

      setIsWinner(type === 'winner');
      showUserList();
    },
    [showUserList, isMobile, showMobileTip],
  );

  return (
    <div className={cx(isMobile && style.mobile)}>
      <div
        className={cx(style.drawCard, isDark && style.isDark, draw.prize_custom && style.isCustom)}>
        <div className={style.title}>
          <div className={cx(style.creator, isOfficialCreator && style.isOfficial)}>
            <Trans i18nKey="DrawCard.createdByOfficial" values={{ name: article._author }}>
              Lucky Draw by{' '}
              {isOfficialCreator ? (
                <OrgVerifiedLogo
                  size={16}
                  className={style.officialIcon}
                  marginLeft={0}
                  marginRight={4}
                  disableTooltip
                  unverified={
                    (article?.creator as OfficialAccount)?.verify_status !==
                    OfficialAccountVerifyStatus.VERIFIED
                  }
                />
              ) : null}{' '}
              {article._author}
            </Trans>
          </div>
          <div className={cx(style.drawStatus, isComplete && style.isSettled)}>
            {isComplete ? t('DrawCard.completed') : t('DrawCard.in-progress')}
          </div>
        </div>
        <div className={cx(style.body, isComplete && !draw.is_joined && style.isMissed)}>
          <div className={style.innerBody}>
            <div className={style.prizeTitle}>
              {draw.prize_custom ? (
                <IconRcStreamPrizeCustom className={style.prizeIcon} />
              ) : (
                <IconRcStreamPrizeCash className={style.prizeIcon} />
              )}
              {`${draw.prize_custom ? t('DrawCard.custom-prize') : t('DrawCard.cash-prize')}`}
            </div>
            <div className={style.prizeDetail}>
              <HelperTooltip
                title={
                  draw.prize_custom ||
                  (draw.prize_value ? formatNetworth(draw.prize_value / 10000, true) : '$0.00')
                }>
                <span className={style.prizeName}>
                  {draw.prize_custom ||
                    (draw.prize_value ? formatNetworth(draw.prize_value / 10000, true) : '$0.00')}
                </span>
              </HelperTooltip>

              <HelperTooltip
                title={t('DrawCard.winnerCount', {
                  count: draw.prize_count,
                  amount: draw._prize_count,
                })}>
                <span className={style.prizeCount}>× {draw._prize_count}</span>
              </HelperTooltip>
            </div>
            {draw.is_winner ? (
              <>
                <div className={style.winTitle}>{t('DrawCard.1jq1ooh')}</div>
                {draw.prize_custom ? (
                  <>
                    <div className={style.thinDesc}>
                      {t('DrawCard.1p86jmm')}
                      {draw.prize_custom_distribution}
                    </div>
                    <div className={style.thinDesc}>{t('DrawCard.1ujj30g')}</div>
                  </>
                ) : (
                  <div className={style.thinDesc}>{t('DrawCard.cenapq')}</div>
                )}
              </>
            ) : draw.is_settled ? (
              draw.is_joined ? (
                <div className={style.thickDesc}>{t('DrawCard.32cijd')}</div>
              ) : null
            ) : isComplete ? (
              draw.is_joined ? (
                <div className={style.thickDesc}>
                  {t('DrawCard.rjvvk')}
                  <div className={style.seperator} />
                  {t('DrawCard.1hic812')}
                </div>
              ) : null
            ) : draw.is_joined ? (
              <div className={style.thickDesc}>
                {t('DrawCard.rjvvk')}
                <div className={style.seperator} />
                {t('DrawCard.vsqee2', { time: endsTime })}
              </div>
            ) : (
              <>
                {article.creator?.id === address ? null : (
                  <HelperTooltip title={hasPermission ? undefined : t('DrawCard.noPermission')}>
                    <Button
                      type="primary"
                      onClick={handleJoinClick}
                      disabled={!article.draw || !hasPermission}
                      className={style.joinBtn}>
                      {t('DrawCard.6jwceh')}
                    </Button>
                  </HelperTooltip>
                )}
                <div className={article.creator?.id === address ? style.thickDesc : style.endsTime}>
                  {t('DrawCard.yos3f', { time: endsTime })}
                </div>
              </>
            )}
          </div>
        </div>
        <div className={style.footer}>
          <RestrictionTip data={draw.permissions} />
          <div className={style.footerRight}>
            <IconRcStreamDrawHot className={style.hotIcon} />
            <span className={style.userCount} onClick={handleUserListClick}>
              {t('DrawCard.participantNum', { amount: formatNum(draw.join_count) })}
            </span>
            {draw.winner_count ? (
              <>
                <div className={style.seperator} />
                <span className={style.userCount} data-type="winner" onClick={handleUserListClick}>
                  {formatNum(draw.winner_count)}{' '}
                  {t('DrawCard.1dupss4', { count: draw.winner_count })}
                </span>
              </>
            ) : null}
          </div>
        </div>
        {draw.is_winner ? <IconRcStreamDrawWinner className={style.winnerIcon} /> : null}
        {on ? <JoinDrawModal on={on} article={article} turnOff={turnOff} /> : null}
        {isUserList ? (
          <DrawJoinersModal
            id={draw.id}
            turnOff={hideUserList}
            on={isUserList}
            isWinner={isWinner}
          />
        ) : null}
        {isMobile && mobileTip ? (
          <MobileModalTip visible={mobileTip} setVisible={setMobileTip} />
        ) : null}
      </div>
    </div>
  );
};
